import React, { useEffect, useState } from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { mergeStyles } from '@fluentui/react';
import {
  Field,
  Label,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
  Input,
  Select,
} from '@fluentui/react-components';
import { Controller, UseFormMethods } from 'react-hook-form';
import IAgmDetail from '../../../../utils/types/IAgmDetail';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import InlineFormInputErrorMessage from '../../../common/errorContent/InlineFormInputErrorMessage';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import CurrencyCodeDropdown from '../../../common/formFields/CurrencyCodeDropdown';
import AuditingFirmsDropdown from './AuditingFirmsDropdown';
import StatDateFormats from '../../../../utils/types/DateFormats';
import momentUtc from '../../../../utils/DateFormatter';
import monthDayFormat from '../../../../utils/MonthDayFormatter';
import {
  yesOption,
  noOption,
  signatureInkColorOptions,
  paperSizeOptions,
  additionalProcessingTimeOptions,
} from '../../../../utils/types/CommonDropdownOptions';
import { RequiredMarker } from '../../../common/formFields/RequiredMarker';
import MultiSelectDropdown from '../../../common/MultiSelectDropdown';

export interface IAgmDetailFormProps {
  agmDetail: IAgmDetail;
  form: UseFormMethods<IAgmDetail>;
}

const finalExternalFilingSubmitterOptions: IDropdownOption[] = [
  { key: 'AUDITOR', text: 'Auditor' },
  { key: 'COUNSEL', text: 'Counsel' },
  { key: 'LOCAL', text: 'Local Finance Team' },
  { key: 'OTHER', text: 'Other' },
];

const AgmDetailForm = ({ agmDetail, form }: IAgmDetailFormProps): JSX.Element => {
  const { control, errors: formErrors, watch, setValue, clearErrors } = form;

  const watchTaxDeadlineBeforeAfsDeadline = watch(
    'taxFilingDeadlineBeforeAfsDeadline',
    agmDetail?.taxFilingDeadlineBeforeAfsDeadline,
  );

  const watchSignatureRequiredDeadline = watch(
    'physicalSignatureRequired',
    agmDetail?.physicalSignatureRequired,
  );

  const watchInkColorRequired = watch('inkColorRequired', agmDetail?.inkColorRequired);

  const watchAuditingFirm = watch('auditingFirm', agmDetail?.auditingFirm);

  const watchFinalExternalFilingSubmitter = watch(
    'finalExternalFilingSubmitter',
    agmDetail?.finalExternalFilingSubmitter,
  );

  const watchPaperSize = watch('paperSize', agmDetail?.paperSize);

  const amountStyles = mergeStyles({
    width: '295px',
  });

  const [selectedAdditionalTimeOptions, setSelectedAdditionalTime] = useState<string[]>(() => {
    const values: string[] = [];
    if (agmDetail?.additionalProcessingTimeApostille) {
      values.push('APOSTILLE');
    }
    if (agmDetail?.additionalProcessingTimeLegalization) {
      values.push('LEGALIZATION');
    }
    if (agmDetail?.additionalProcessingTimeLocalSigner) {
      values.push('LOCALSIGNER');
    }
    return values;
  });

  useEffect(() => {
    setValue('additionalProcessingTime', selectedAdditionalTimeOptions);
  }, [selectedAdditionalTimeOptions]);

  const requiredMessageStyles = { color: '#A4262C', fontSize: 12 };
  const requiredLabelStyles = { paddingBottom: 0 };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <div style={requiredMessageStyles}>* Fields required to start the review</div>

        <Controller
          name="agmDeadlineBeforeAfsDeadline"
          defaultValue={agmDetail?.agmDeadlineBeforeAfsDeadline}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether the AGM deadline is earlier than the AFS deadline',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field
                  label={`Is the AGM deadline (${monthDayFormat(
                    agmDetail?.annualGeneralMeetingDueDateMonth,
                    agmDetail?.annualGeneralMeetingDueDateDay,
                  )}) earlier than the AFS deadline (${momentUtc(
                    agmDetail?.filingDueDate,
                    StatDateFormats.DaySimple,
                  )})?`}
                  required
                >
                  <RadioGroup
                    id="agmDeadlineBeforeAfsDeadline"
                    name="agmDeadlineBeforeAfsDeadline"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      const boolValue = radioData.value === 'true';
                      onChange(boolValue);
                      if (radioData.value === 'false') {
                        setValue('agmDeadline', null);
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.agmDeadlineBeforeAfsDeadline?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="taxFilingDeadlineBeforeAfsDeadline"
          defaultValue={agmDetail?.taxFilingDeadlineBeforeAfsDeadline}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether the tax filing deadline is earlier than the AFS deadline',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field
                  label={`Is the tax filing deadline earlier than the AFS deadline (${momentUtc(
                    agmDetail?.filingDueDate,
                    StatDateFormats.DaySimple,
                  )})?`}
                  required
                >
                  <RadioGroup
                    id="taxFilingDeadlineBeforeAfsDeadline"
                    name="taxFilingDeadlineBeforeAfsDeadline"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      const boolValue = radioData.value === 'true';
                      onChange(boolValue);
                      if (radioData.value === 'false') {
                        setValue('taxFilingDeadline', null);
                        clearErrors('taxFilingDeadline');
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.taxFilingDeadlineBeforeAfsDeadline?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="taxFilingDeadline"
          control={control}
          defaultValue={agmDetail?.taxFilingDeadline}
          rules={{
            validate: (value) =>
              watchTaxDeadlineBeforeAfsDeadline !== true ||
              (typeof value === 'string' && value.trim() !== '') ||
              'Please provide the Tax Filing Deadline',
          }}
          render={({ value, onChange }): React.ReactElement => (
            <UtcDatePicker
              label="Tax Filing Deadline"
              isRequired={watchTaxDeadlineBeforeAfsDeadline === true}
              disabled={watchTaxDeadlineBeforeAfsDeadline !== true}
              allowTextInput
              disableAutoFocus={false}
              value={value}
              textField={{
                name: 'taxFilingDeadline',
                errorMessage: formErrors?.taxFilingDeadline?.message,
              }}
              onSelectDate={(utcDateString: string): void => {
                onChange(utcDateString);
              }}
            />
          )}
        />
        <Controller
          name="externalAfsFilingRequired"
          defaultValue={agmDetail?.externalAfsFilingRequired}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether a formal AFS external filing is required',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field label="Is there a formal AFS external filing required?" required>
                  <RadioGroup
                    id="externalAfsFilingRequired"
                    name="externalAfsFilingRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      onChange(radioData.value === 'true');
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.externalAfsFilingRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="requiredToBeFiledWithAfs"
          defaultValue={agmDetail?.requiredToBeFiledWithAfs}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether the AGM date and FS date have to be aligned',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field label="Do the AGM date and the FS date have to be aligned?" required>
                  <RadioGroup
                    id="requiredToBeFiledWithAfs"
                    name="requiredToBeFiledWithAfs"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      onChange(radioData.value === 'true');
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.requiredToBeFiledWithAfs?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="canAfsBeExtended"
          defaultValue={agmDetail?.canAfsBeExtended}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether the AFS deadline can be extended',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field label="Can the AFS deadline be extended?" required>
                  <RadioGroup
                    id="canAfsBeExtended"
                    name="canAfsBeExtended"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      onChange(radioData.value === 'true');
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage errorMessage={formErrors?.canAfsBeExtended?.message} />
              </>
            );
          }}
        />
        <Controller
          name="physicalSignatureRequired"
          defaultValue={agmDetail?.physicalSignatureRequired}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether a hard copy signature is required for filing',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field label="Is a hard copy signature required for filing?" required>
                  <RadioGroup
                    id="physicalSignatureRequired"
                    name="physicalSignatureRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      onChange(radioData.value === 'true');
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.physicalSignatureRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="hardCopyShippingAddress"
          control={control}
          defaultValue={agmDetail?.hardCopyShippingAddress || ''}
          rules={{
            validate: (value) =>
              watchSignatureRequiredDeadline !== true ||
              (value && value.trim() !== '') ||
              'Please provide the Address for Hard Copies',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Address for Hard Copies"
              required={watchSignatureRequiredDeadline === true}
              validationMessage={formErrors?.hardCopyShippingAddress?.message}
            >
              <Textarea
                id="hardCopyShippingAddress"
                disabled={watchSignatureRequiredDeadline !== true}
                value={value}
                onChange={(e, data) => onChange(data.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="hardCopyShippingContactName"
          control={control}
          defaultValue={agmDetail?.hardCopyShippingContactName || ''}
          rules={{
            validate: (value) =>
              watchSignatureRequiredDeadline !== true ||
              (value && value.trim() !== '') ||
              'Please provide the Contact Name',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Contact Name"
              required={watchSignatureRequiredDeadline === true}
              validationMessage={formErrors?.hardCopyShippingContactName?.message}
            >
              <Input
                id="hardCopyShippingContactName"
                disabled={watchSignatureRequiredDeadline !== true}
                value={value}
                type="text"
                onChange={(e, data) => onChange(data.value)}
                name={name}
              />
            </Field>
          )}
        />
        <Controller
          name="hardCopyShippingPhoneNumber"
          control={control}
          defaultValue={agmDetail?.hardCopyShippingPhoneNumber || ''}
          rules={{
            validate: (value) =>
              watchSignatureRequiredDeadline !== true ||
              (value && value.trim() !== '') ||
              'Please provide the Phone Number',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Phone Number"
              required={watchSignatureRequiredDeadline === true}
              validationMessage={formErrors?.hardCopyShippingPhoneNumber?.message}
            >
              <Input
                id="hardCopyShippingPhoneNumber"
                disabled={watchSignatureRequiredDeadline !== true}
                value={value}
                type="text"
                onChange={(e, data) => onChange(data.value)}
                name={name}
              />
            </Field>
          )}
        />
        <Controller
          name="inkColorRequired"
          defaultValue={agmDetail?.inkColorRequired}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether there is a required ink color for the signatures',
          }}
          render={({ onChange }) => {
            return (
              <>
                <Field label="Is there a required ink color for the signatures?" required>
                  <RadioGroup
                    id="inkColorRequired"
                    name="inkColorRequired"
                    value={watchInkColorRequired?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      const boolValue = radioData.value === 'true';
                      onChange(boolValue);
                      if (radioData.value === 'false') {
                        setValue('signatureInkColor', null);
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage errorMessage={formErrors?.inkColorRequired?.message} />
              </>
            );
          }}
        />
        <Controller
          name="signatureInkColor"
          control={control}
          defaultValue={agmDetail?.signatureInkColor}
          rules={{
            validate: (value) =>
              watchInkColorRequired !== true ||
              (value && value !== '') ||
              'Please select a Signature Ink Color',
          }}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field
              label="Signature Ink Color"
              validationMessage={formErrors?.signatureInkColor?.message}
              required={watchInkColorRequired === true}
            >
              <Select
                onChange={(event, data): void => {
                  onChange(data.value);
                  clearErrors('signatureInkColor');
                }}
                value={value || ''}
                disabled={watchInkColorRequired !== true}
              >
                <option value="" disabled>
                  Select an option
                </option>
                {signatureInkColorOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        />
        <Controller
          name="originalsCount"
          control={control}
          defaultValue={agmDetail?.originalsCount?.toString() || ''}
          rules={{
            min: {
              value: 0,
              message: 'Please enter a positive number',
            },
            required: 'Please provide the number of original documents required',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="How many originals are required?"
              required
              validationMessage={formErrors?.originalsCount?.message}
            >
              <Input
                name={name}
                type="number"
                value={value}
                onChange={(e, data) => onChange(data.value)}
              />
            </Field>
          )}
        />
        <Controller
          name="paperSize"
          control={control}
          defaultValue={agmDetail?.paperSize}
          rules={{
            validate: (value) =>
              value !== undefined && value !== null && value !== ''
                ? true
                : 'Please select a Paper Size',
          }}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field label="Paper Size" required validationMessage={formErrors?.paperSize?.message}>
              <Select
                onChange={(event, data): void => {
                  const selectedValue = data.value;
                  onChange(selectedValue);
                  clearErrors('paperSize');
                  if (selectedValue !== 'OTHER') {
                    setValue('otherPaperSize', '');
                    clearErrors('otherPaperSize');
                  }
                }}
                value={value || ''}
              >
                <option value="" disabled>
                  Select an option
                </option>
                {paperSizeOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        />
        <Controller
          name="otherPaperSize"
          control={control}
          defaultValue={agmDetail?.otherPaperSize || ''}
          rules={{
            validate: (value) =>
              watchPaperSize !== 'OTHER' ||
              (value && value.trim() !== '') ||
              'Please provide the Other Paper Size',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Other Paper Size"
              required={watchPaperSize === 'OTHER'}
              validationMessage={formErrors?.otherPaperSize?.message}
            >
              <Textarea
                id="otherPaperSize"
                disabled={watchPaperSize !== 'OTHER'}
                value={value}
                onChange={(e, data) => onChange(data.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="managementRepresentationLetterRequired"
          defaultValue={agmDetail?.managementRepresentationLetterRequired}
          control={control}
          rules={{
            validate: (value) =>
              typeof value === 'boolean' ||
              'Please indicate whether a Management Representation Letter is required',
          }}
          render={({ value, onChange }): React.ReactElement => {
            return (
              <>
                <Field label="Is a Management Representation Letter Required?" required>
                  <RadioGroup
                    id="managementRepresentationLetterRequired"
                    name="managementRepresentationLetterRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      onChange(radioData.value === 'true');
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={formErrors?.managementRepresentationLetterRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="finalExternalFilingSubmitter"
          control={control}
          defaultValue={agmDetail?.finalExternalFilingSubmitter}
          rules={{
            validate: (value) =>
              value !== undefined && value !== null && value !== ''
                ? true
                : 'Please select who is submitting the final external filing',
          }}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field
              label="Who is submitting the final external filing?"
              required
              validationMessage={formErrors?.finalExternalFilingSubmitter?.message}
            >
              <Select
                onChange={(event, data): void => {
                  const selectedValue = data.value;
                  onChange(selectedValue);
                  clearErrors('finalExternalFilingSubmitter');
                  if (selectedValue !== 'OTHER') {
                    setValue('otherFinalExternalFilingSubmitter', '');
                  }
                }}
                value={value || ''}
              >
                <option value="" disabled>
                  Select an option
                </option>
                {finalExternalFilingSubmitterOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        />
        <Controller
          name="otherFinalExternalFilingSubmitter"
          control={control}
          defaultValue={agmDetail?.otherFinalExternalFilingSubmitter || ''}
          rules={{
            validate: (value) =>
              watchFinalExternalFilingSubmitter !== 'OTHER' ||
              (value && value.trim() !== '') ||
              'Please provide the Other Final External Filing Submitter',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Other Final External Filing Submitter"
              required={watchFinalExternalFilingSubmitter === 'OTHER'}
              validationMessage={formErrors?.otherFinalExternalFilingSubmitter?.message}
            >
              <Textarea
                id="otherFinalExternalFilingSubmitter"
                disabled={watchFinalExternalFilingSubmitter !== 'OTHER'}
                value={value}
                onChange={(e, data) => onChange(data.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="auditingFirm"
          control={control}
          defaultValue={agmDetail?.auditingFirm}
          rules={{
            validate: (value) => (value ? true : 'Please select an Auditing Firm'),
          }}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <>
              <Label style={requiredLabelStyles}>
                Auditing Firm <RequiredMarker />
              </Label>
              <AuditingFirmsDropdown
                valueKey="auditingFirm"
                ariaLabel="Auditing Firm Dropdown"
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                handleChange={(newValue: any, valueKey: string): void => {
                  setValue('auditingFirm', newValue);
                  if (newValue.fieldName !== 'Other') {
                    setValue('otherAuditingFirm', '');
                  }
                }}
                errors={formErrors}
              />
            </>
          )}
        />
        <Controller
          name="otherAuditingFirm"
          control={control}
          defaultValue={agmDetail?.otherAuditingFirm || ''}
          rules={{
            validate: (value) =>
              watchAuditingFirm?.fieldName !== 'Other' ||
              (value && value.trim() !== '') ||
              'Please provide the Other Auditing Firm',
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Other Auditing Firm"
              required={watchAuditingFirm?.fieldName === 'Other'}
              validationMessage={formErrors?.otherAuditingFirm?.message}
            >
              <Textarea
                id="otherAuditingFirm"
                disabled={watchAuditingFirm?.fieldName !== 'Other'}
                value={value}
                onChange={(e, data) => onChange(data.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="auditorAppointmentPeriod"
          control={control}
          defaultValue={agmDetail?.auditorAppointmentPeriod || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Period of Auditor Appointment"
              required
              validationMessage={formErrors?.auditorAppointmentPeriod?.message}
            >
              <Textarea
                id="auditorAppointmentPeriod"
                value={value}
                onChange={(e, data) => onChange(data.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
          rules={{
            required: 'Please provide a Period of Auditor Appointment',
          }}
        />
        <Controller
          name="additionalProcessingTime"
          control={control}
          defaultValue={selectedAdditionalTimeOptions}
          render={() => {
            return (
              <>
                <MultiSelectDropdown
                  label="Additional Processing Time"
                  onChange={(event, selectedItems): void => {
                    setSelectedAdditionalTime(selectedItems.map((item) => item.key.toString()));
                  }}
                  options={additionalProcessingTimeOptions}
                  selectedKeys={selectedAdditionalTimeOptions}
                  placeholder="Select an option"
                  errorMessage={formErrors?.additionalProcessingTimeApostille?.message}
                />
              </>
            );
          }}
        />
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
          <Stack>
            <Controller
              name="auditorFees"
              control={control}
              defaultValue={agmDetail?.auditorFees?.toString() || ''}
              className={amountStyles}
              rules={{
                min: {
                  value: 0,
                  message: 'Please enter a positive number',
                },
                required: 'Please enter the Auditor Fees Amount',
              }}
              render={({ onChange, value, name }): React.ReactElement => (
                <Field
                  label="Auditor Fees"
                  required
                  validationMessage={formErrors?.auditorFees?.message}
                >
                  <Input
                    name={name}
                    type="number"
                    value={value}
                    onChange={(e, data) => onChange(data.value)}
                    className={amountStyles}
                  />
                </Field>
              )}
            />
          </Stack>
          <StackItem>
            <Controller
              name="auditorFeesCurrencyCode"
              control={control}
              rules={{
                required: 'Please provide a Currency Code',
              }}
              render={({ onChange, onBlur, value, name }): React.ReactElement => (
                <CurrencyCodeDropdown
                  label="Currency"
                  propertyName="auditorFeesCurrencyCode"
                  handleChange={(newValue: string, valueKey: string): void => {
                    onChange(newValue);
                  }}
                  defaultValue={value || agmDetail?.auditorFeesCurrencyCode}
                  errorMessage={formErrors?.auditorFeesCurrencyCode?.message}
                  required
                />
              )}
              defaultValue={agmDetail?.auditorFeesCurrencyCode}
            />
          </StackItem>
        </Stack>
      </Stack>
    </FluentProvider>
  );
};

export default AgmDetailForm;
